import { useState, useCallback } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import { toast } from "sonner";
import { generateBlurhash } from "@/lib/blurhash-utils";
import { convertImageFormat } from "@/lib/image-utils";

// use high quality for JPG conversion
const JPG_QUALITY = 0.95;

interface UploadResult {
  key: string;
  filename: string;
  contentType?: string;
  size?: number;
  blurhash?: string;
}

interface UseConvexImageUploadOptions {
  maxFileSize?: number;
  allowedTypes?: string[];
  reffId?: string;
  onSuccess?: (results: UploadResult[]) => void;
  onError?: (error: string) => void;
}

interface UseConvexImageUploadReturn {
  upload: (files: File[]) => Promise<UploadResult[]>;
  uploadSingle: (file: File) => Promise<UploadResult>;
  isUploading: boolean;
  uploadProgress: number;
  error: string | null;
  reset: () => void;
}

export function useConvexImageUpload(
  options: UseConvexImageUploadOptions = {},
): UseConvexImageUploadReturn {
  const {
    maxFileSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"],
    reffId,
    onSuccess,
    onError,
  } = options;

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  // use convex r2 upload mutation
  const generatePropertyUploadUrl = useMutation(
    api.r2.generatePropertyImageUploadUrl,
  );

  const validateFile = useCallback(
    (file: File): string | null => {
      if (file.size > maxFileSize) {
        return `file size must be less than ${Math.round(maxFileSize / 1024 / 1024)}MB`;
      }

      if (!allowedTypes.includes(file.type)) {
        return `file type ${file.type} is not allowed`;
      }

      return null;
    },
    [maxFileSize, allowedTypes],
  );

  const uploadToConvex = useCallback(
    async (file: File): Promise<UploadResult> => {
      if (!reffId) {
        throw new Error("reffId is required for uploading images");
      }

      // validate file
      const validationError = validateFile(file);
      if (validationError) {
        throw new Error(validationError);
      }

      try {
        // convert image to JPG format
        let jpgFile: File;
        try {
          jpgFile = await convertImageFormat(file, "jpeg", JPG_QUALITY);
        } catch (conversionError) {
          console.warn(
            `Failed to convert ${file.name} to JPG, using original:`,
            conversionError,
          );
          jpgFile = file;
        }

        // generate blurhash
        let blurhash: string | undefined;
        try {
          blurhash = await generateBlurhash(jpgFile);
        } catch (blurhashError) {
          console.warn(
            `Failed to generate blurhash for ${jpgFile.name}:`,
            blurhashError,
          );
        }

        // get upload url from convex with reffId (ensure .jpg extension)
        const jpgFilename = `${file.name.split(".")[0]}.jpg`;
        const { url, key } = await generatePropertyUploadUrl({
          reffId,
          filename: jpgFilename,
        });

        // upload converted JPG file to r2
        const uploadResponse = await fetch(url, {
          method: "PUT",
          body: jpgFile,
          headers: {
            "Content-Type": "image/jpeg",
          },
        });

        if (!uploadResponse.ok) {
          throw new Error(
            `Upload failed with status: ${uploadResponse.status}`,
          );
        }

        return {
          key,
          filename: jpgFilename,
          contentType: "image/jpeg",
          size: jpgFile.size,
          blurhash,
        };
      } catch (error) {
        console.error("convex upload error:", error);
        throw new Error("failed to upload image");
      }
    },
    [validateFile, generatePropertyUploadUrl, reffId],
  );

  const uploadSingle = useCallback(
    async (file: File): Promise<UploadResult> => {
      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      try {
        setUploadProgress(50);
        const result = await uploadToConvex(file);
        setUploadProgress(100);

        onSuccess?.([result]);
        toast.success(`uploaded ${file.name} successfully`);

        return result;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "upload failed";
        setError(errorMessage);
        onError?.(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsUploading(false);
        setTimeout(() => setUploadProgress(0), 1000);
      }
    },
    [uploadToConvex, onSuccess, onError],
  );

  const upload = useCallback(
    async (files: File[]): Promise<UploadResult[]> => {
      if (files.length === 0) {
        return [];
      }

      setIsUploading(true);
      setError(null);
      setUploadProgress(0);

      try {
        const results: UploadResult[] = [];
        const totalFiles = files.length;

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setUploadProgress(Math.round(((i + 0.5) / totalFiles) * 100));

          const result = await uploadToConvex(file);
          results.push(result);

          setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
        }

        onSuccess?.(results);
        toast.success(
          `uploaded ${files.length} file${files.length > 1 ? "s" : ""} successfully`,
        );

        return results;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "upload failed";
        setError(errorMessage);
        onError?.(errorMessage);
        toast.error(errorMessage);
        throw err;
      } finally {
        setIsUploading(false);
        setTimeout(() => setUploadProgress(0), 1000);
      }
    },
    [uploadToConvex, onSuccess, onError],
  );

  const reset = useCallback(() => {
    setError(null);
    setUploadProgress(0);
    setIsUploading(false);
  }, []);

  return {
    upload,
    uploadSingle,
    isUploading,
    uploadProgress,
    error,
    reset,
  };
}

// helper hook for property images specifically
export function usePropertyImageUpload(reffId?: string) {
  return useConvexImageUpload({
    maxFileSize: 10 * 1024 * 1024, // 10MB for property images
    allowedTypes: ["image/jpeg", "image/jpg", "image/png", "image/webp"],
    reffId,
  });
}
